/**
 * TurnTimeline: the turn breakdown at the bottom of the Graph view's side panel. A compact vertical timeline of the chat's turns,
 * newest last: what was asked and answered, the facts learned, the leading cause with its move, the causes ruled out. The live
 * turn is marked; clicking a turn pins the graph to it (lights, sidebar, caption as of that turn); "Back to live" unpins.
 *
 * Keyboard on the list: ArrowUp / ArrowDown pin the previous / next turn, Home the first, End live; Enter / Space pin the focused
 * one. Collapses to a one-line summary ("Turn 5 of 5 · Live"); the collapsed state is remembered per browser.
 *
 * Props: turns (useDiagHistory entries), pinnedSeq (null = live), onPin(seq | null).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { LIGHTS_TEXT, formatPercent } from "./lightsText";
import { describeTurns } from "./turnTimelineLogic";

const COLLAPSED_KEY = "d5.lights.turns.collapsed";
const ICON_PX = 14;
const KEY = Object.freeze({ UP: "ArrowUp", DOWN: "ArrowDown", HOME: "Home", END: "End" });
const T = LIGHTS_TEXT.turns;

const readCollapsed = () => {
  try { return window.localStorage.getItem(COLLAPSED_KEY) === "1"; } catch (_) { return false; }
};
const writeCollapsed = (v) => {
  try { window.localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0"); } catch (_) { /* storage is optional */ }
};

function Delta({ delta }) {
  if (delta == null || delta === 0) return null;
  const up = delta > 0;
  return (
    <span className={`d5-lights-turn-delta ${up ? "d5-lights-turn-delta--up" : "d5-lights-turn-delta--down"}`} aria-label={T.deltaLabel(delta)}>
      <span aria-hidden="true">{up ? "▲" : "▼"}</span> {up ? "+" : ""}{delta}
    </span>
  );
}

function TurnItem({ turn, shown, pinned, open, onSelect, onToggle }) {
  const { answers, facts, lead, ruledOut } = turn;
  const quiet = !answers.length && !facts.length && !ruledOut.length && !(lead && (lead.delta || lead.changed));
  return (
    <li className={`d5-lights-turn${shown ? " d5-lights-turn--shown" : ""}${turn.live ? " d5-lights-turn--live" : ""}`} aria-current={shown ? "true" : undefined}>
      <button type="button" className="d5-lights-turn-btn" tabIndex={shown ? 0 : -1} aria-pressed={pinned || undefined}
        onClick={() => onSelect(turn)} data-seq={turn.seq}>
        <span className="d5-lights-turn-mark" aria-hidden="true" />
        <span className="d5-lights-turn-head">
          <span>{T.turn(turn.n)}</span>
          {turn.live && <span className="d5-lights-chip d5-lights-chip--live">{turn.ended ? T.final : T.live}</span>}
          {pinned && <span className="d5-lights-chip d5-lights-chip--pinned">{T.pinned}</span>}
        </span>
        {!open && lead && (
          <span className="d5-lights-turn-line d5-lights-turn-line--brief">
            <span className="d5-lights-turn-lead">{lead.label}</span>
            <b>{formatPercent(lead.pct)}</b>
            <Delta delta={lead.delta} />
          </span>
        )}
        {open && answers.map((a, i) => (
          <span key={i} className="d5-lights-turn-qa">
            {a.question && <span className="d5-lights-turn-q">{a.question}</span>}
            {a.answer && <span className="d5-lights-turn-a">{a.answer}</span>}
          </span>
        ))}
        {open && lead && (
          <span className="d5-lights-turn-line">
            <span className="d5-lights-turn-key">{lead.changed ? T.nowLeading : T.leading}</span>
            <span className="d5-lights-turn-lead">{lead.label}</span>
            <b>{formatPercent(lead.pct)}</b>
            <Delta delta={lead.delta} />
          </span>
        )}
        {open && facts.length > 0 && (
          <span className="d5-lights-turn-line">
            <span className="d5-lights-turn-key">{T.learned}</span>
            <span>{facts.map((f) => (f.value === "no" ? T.factNo(f.label) : f.label)).join(" · ")}</span>
          </span>
        )}
        {open && ruledOut.length > 0 && (
          <span className="d5-lights-turn-line d5-lights-turn-line--ruled">
            <span className="d5-lights-turn-key">{T.ruledOut}</span>
            <span>{ruledOut.join(" · ")}</span>
          </span>
        )}
        {open && quiet && <span className="d5-lights-turn-line d5-lights-muted">{T.noChange}</span>}
      </button>
      <button type="button" className="d5-lights-turn-toggle" aria-expanded={open} aria-label={open ? T.collapseTurn(turn.n) : T.expandTurn(turn.n)}
        title={open ? T.collapseTurn(turn.n) : T.expandTurn(turn.n)} onClick={() => onToggle(turn.seq)}>
        {open ? <ChevronUp size={ICON_PX} aria-hidden="true" /> : <ChevronDown size={ICON_PX} aria-hidden="true" />}
      </button>
    </li>
  );
}

export default function TurnTimeline({ turns, pinnedSeq, onPin }) {
  const described = useMemo(() => describeTurns(turns), [turns]);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [openSeqs, setOpenSeqs] = useState(() => new Set());
  const listRef = useRef(null);
  const total = described.length;
  const shownIndex = pinnedSeq == null ? total - 1 : described.findIndex((t) => t.seq === pinnedSeq);
  const shown = shownIndex >= 0 ? shownIndex : total - 1;
  const pinned = pinnedSeq != null && shownIndex >= 0;

  const toggle = useCallback(() => setCollapsed((c) => { writeCollapsed(!c); return !c; }), []);

  // The newest turn scrolls into view while following live; a pinned turn stays where the engineer put it.
  useEffect(() => {
    if (collapsed || pinned || !listRef.current) return;
    const last = listRef.current.lastElementChild;
    if (last && typeof last.scrollIntoView === "function") last.scrollIntoView({ block: "nearest" });
  }, [total, collapsed, pinned]);

  // Each turn starts collapsed to its head and leading cause; its chevron opens or closes the details. Clicking the card only selects the turn.
  const toggleTurn = useCallback((seq) => {
    setOpenSeqs((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq); else next.add(seq);
      return next;
    });
  }, []);
  const selectTurn = useCallback((turn) => onPin(turn.live ? null : turn.seq), [onPin]);

  const pinIndex = useCallback((i) => {
    const target = described[Math.max(0, Math.min(total - 1, i))];
    if (!target) return;
    onPin(target.live ? null : target.seq);
    const btn = listRef.current?.querySelector(`[data-seq="${target.seq}"]`);
    if (btn) btn.focus();
  }, [described, total, onPin]);

  const onKeyDown = (e) => {
    const moves = { [KEY.UP]: shown - 1, [KEY.DOWN]: shown + 1, [KEY.HOME]: 0, [KEY.END]: total - 1 };
    if (!(e.key in moves)) return;
    e.preventDefault();
    e.stopPropagation();
    pinIndex(moves[e.key]);
  };

  if (!total) return null;
  const summary = pinned ? T.summaryPinned(shown + 1, total) : T.summaryLive(total);
  return (
    <section className="d5-lights-turns" aria-label={T.label}>
      <div className="d5-lights-turns-bar">
        <button type="button" className="d5-lights-turns-toggle" aria-expanded={!collapsed} onClick={toggle}>
          {collapsed ? <ChevronUp size={ICON_PX} aria-hidden="true" /> : <ChevronDown size={ICON_PX} aria-hidden="true" />}
          <span className="d5-lights-turns-summary">{summary}</span>
        </button>
        {pinned && <button type="button" className="d5-lights-turns-live" onClick={() => onPin(null)}>{T.backToLive}</button>}
      </div>
      {!collapsed && (
        <ol className="d5-lights-turns-list" ref={listRef} onKeyDown={onKeyDown} aria-label={T.listLabel}>
          {described.map((turn, i) => (
            <TurnItem key={turn.seq} turn={turn} shown={i === shown} pinned={pinned && i === shown} open={openSeqs.has(turn.seq)} onSelect={selectTurn} onToggle={toggleTurn} />
          ))}
        </ol>
      )}
    </section>
  );
}
